import React, { useEffect, useState } from "react";
import { BellOff, Bell } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { toast } from "sonner";
import api from "../../lib/apiClient";

// CommsSnoozeCard — per-topic re-engagement nudge mute.
// Server returns ISO deadlines per topic (already filtered to "still
// active"); we render a row per topic with three quick actions:
// 7 days / 30 days / Forever, plus an "Unsnooze" button when active.
//
// State is the slim `comms_snooze: { topic: iso }` dict from /me/prefs.
// PATCH /me/prefs accepts integer-days OR null to clear — never raw ISO.

const TOPICS = [
  { key: "all",         label: "Pause everything",        sub: "Master switch — silences every category below." },
  { key: "topup",       label: "Top-up reminders",        sub: "Re-engagement when you abandon a Stripe checkout." },
  { key: "gift",        label: "Gift reminders",          sub: "Re-pings when an unclaimed gift is waiting for you." },
  { key: "onboarding",  label: "Onboarding emails",       sub: "Day-3 / Day-7 / Day-14 nudges to record your first clip." },
  { key: "milestones",  label: "Milestones & celebrations", sub: "Saves-count milestones and applause stats." },
  { key: "inbox",       label: "Inbox replies",           sub: "Email mirror when an admin replies in /inbox." },
];

const PRESETS = [
  { days: 7,     label: "7 days" },
  { days: 30,    label: "30 days" },
  { days: 99999, label: "Forever" },
];

const fmtDeadline = (iso) => {
  if (!iso) return null;
  if (iso.startsWith("2099-")) return "forever";
  try {
    const d = new Date(iso);
    return `until ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  } catch {
    return null;
  }
};

const CommsSnoozeCard = () => {
  const [snooze, setSnooze] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // `${topic}:${days}`

  const fetchPrefs = async () => {
    try {
      const { data } = await api.get("/me/prefs");
      setSnooze(data.comms_snooze || {});
    } catch (_) {
      // ignore — leave snooze empty
    }
    setLoading(false);
  };

  useEffect(() => { fetchPrefs(); }, []);

  const patch = async (topic, days) => {
    const key = `${topic}:${days ?? "off"}`;
    setBusy(key);
    try {
      const { data } = await api.patch("/me/prefs", {
        comms_snooze: { [topic]: days },
      });
      setSnooze(data.comms_snooze || {});
      toast.success(days ? "Snoozed." : "Un-snoozed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update preference.");
    }
    setBusy(null);
  };

  if (loading) {
    return (
      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="p-6 text-gray-400 text-sm">Loading notification preferences…</CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gray-900 border-gray-800" data-testid="comms-snooze-card">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <BellOff className="h-5 w-5 text-emerald-400" /> Notification snooze
        </CardTitle>
        <CardDescription className="text-gray-400">
          Pause re-engagement emails when you don't need them. Each topic respects its own
          deadline; "Pause everything" trumps the rest while it's on.
        </CardDescription>
      </CardHeader>
      <CardContent className="divide-y divide-gray-800">
        {TOPICS.map((t) => {
          const active = snooze[t.key];
          const deadline = fmtDeadline(active);
          return (
            <div
              key={t.key}
              className="py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3"
              data-testid={`snooze-row-${t.key}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white flex items-center gap-2">
                  {t.label}
                  {active && (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30"
                      data-testid={`snooze-status-${t.key}`}
                    >
                      <BellOff className="h-3 w-3" /> {deadline}
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-500 leading-snug">{t.sub}</p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {PRESETS.map((p) => (
                  <Button
                    key={p.days}
                    size="sm"
                    onClick={() => patch(t.key, p.days)}
                    disabled={busy === `${t.key}:${p.days}`}
                    className="h-7 text-[11px] px-2.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300"
                    data-testid={`snooze-btn-${t.key}-${p.days}`}
                  >
                    {p.label}
                  </Button>
                ))}
                {active && (
                  <Button
                    size="sm"
                    onClick={() => patch(t.key, null)}
                    disabled={busy === `${t.key}:off`}
                    className="h-7 text-[11px] px-2.5 bg-emerald-700 hover:bg-emerald-600 text-white"
                    data-testid={`snooze-clear-${t.key}`}
                  >
                    <Bell className="h-3 w-3 mr-1" /> Resume
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default CommsSnoozeCard;
